import { useEffect, useState } from "react";
import { Layout } from "@/components/layout/layout";
import { useHealthCheck, ApiError, getHealthCheckQueryKey } from "@workspace/api-client-react";
import type { HealthStatus } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, Database, Server, Clock, Tag } from "lucide-react";

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const s = Math.floor(seconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export default function Status() {
  const {
    data,
    error,
    isLoading,
    isError,
    dataUpdatedAt,
    errorUpdatedAt,
  } = useHealthCheck({
    query: {
      queryKey: getHealthCheckQueryKey(),
      refetchInterval: 30_000,
      refetchIntervalInBackground: true,
      retry: false,
    },
  });

  const lastChecked = Math.max(dataUpdatedAt ?? 0, errorUpdatedAt ?? 0);

  const errorPayload: HealthStatus | null =
    isError && error instanceof ApiError && error.data && typeof error.data === "object"
      ? (error.data as HealthStatus)
      : null;
  const apiErrorStatus = isError && error instanceof ApiError ? error.status : null;
  const effective: HealthStatus | null = errorPayload ?? data ?? null;
  const [lastCheckedLabel, setLastCheckedLabel] = useState<string>("—");

  useEffect(() => {
    function update() {
      if (!lastChecked) {
        setLastCheckedLabel("—");
        return;
      }
      const diffMs = Date.now() - lastChecked;
      const sec = Math.max(0, Math.floor(diffMs / 1000));
      if (sec < 5) setLastCheckedLabel("just now");
      else if (sec < 60) setLastCheckedLabel(`${sec}s ago`);
      else {
        const m = Math.floor(sec / 60);
        setLastCheckedLabel(m === 1 ? "1 minute ago" : `${m} minutes ago`);
      }
    }
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [lastChecked]);

  const apiReachable = !isError || errorPayload !== null;
  const apiOk = apiReachable;
  const dbOk = apiReachable && effective?.db === "ok";
  const allOk = apiOk && dbOk && !!effective?.ok;

  let headlineTitle: string;
  let headlineDescription: string;
  let headlineTone: "ok" | "warn" | "down";

  if (isLoading && !effective) {
    headlineTitle = "Checking system status…";
    headlineDescription = "Pinging the service.";
    headlineTone = "warn";
  } else if (allOk) {
    headlineTitle = "All systems operational";
    headlineDescription = "The site and database are healthy.";
    headlineTone = "ok";
  } else if (!apiReachable) {
    headlineTitle = "API unavailable";
    headlineDescription =
      apiErrorStatus !== null
        ? `The service responded with HTTP ${apiErrorStatus}. Please try again shortly.`
        : "We couldn't reach the service. Please try again shortly.";
    headlineTone = "down";
  } else if (!dbOk) {
    headlineTitle = "Database unavailable";
    headlineDescription = "The site is up but the database isn't responding.";
    headlineTone = "down";
  } else {
    headlineTitle = "Service degraded";
    headlineDescription = "The service reported a problem. Please try again shortly.";
    headlineTone = "down";
  }

  const headlineColor =
    headlineTone === "ok"
      ? "text-emerald-600 dark:text-emerald-400"
      : headlineTone === "warn"
      ? "text-amber-600 dark:text-amber-400"
      : "text-red-600 dark:text-red-400";

  const HeadlineIcon = headlineTone === "ok" ? CheckCircle2 : AlertTriangle;

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-foreground tracking-tight">System Status</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Live health of the Interclub Poker Circuit service. Refreshes every 30 seconds.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <Card>
          <CardContent className="py-8">
            <div className="flex items-start gap-4">
              <HeadlineIcon className={`h-10 w-10 shrink-0 ${headlineColor}`} aria-hidden />
              <div>
                <h2 className={`text-2xl font-semibold ${headlineColor}`} data-testid="status-headline">
                  {headlineTitle}
                </h2>
                <p className="mt-1 text-muted-foreground">{headlineDescription}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Last checked: <span data-testid="status-last-checked">{lastCheckedLabel}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <StatusRow
            icon={<Server className="h-5 w-5" />}
            label="API"
            ok={apiOk}
            okText="Operational"
            downText="Unreachable"
            loading={isLoading && !effective}
          />
          <StatusRow
            icon={<Database className="h-5 w-5" />}
            label="Database"
            ok={dbOk}
            okText="Operational"
            downText="Unavailable"
            loading={isLoading && !effective}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Service info</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <InfoRow
              icon={<Clock className="h-4 w-4" />}
              label="Uptime"
              value={effective ? formatUptime(effective.uptime) : "—"}
            />
            <InfoRow
              icon={<Tag className="h-4 w-4" />}
              label="Version"
              value={effective?.version ?? "—"}
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function StatusRow({
  icon,
  label,
  ok,
  okText,
  downText,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  ok: boolean;
  okText: string;
  downText: string;
  loading: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground">{icon}</span>
            <span className="font-medium">{label}</span>
          </div>
          {loading ? (
            <Badge variant="secondary">Checking…</Badge>
          ) : ok ? (
            <Badge className="bg-emerald-600 text-white hover:bg-emerald-600" data-testid={`status-${label.toLowerCase()}-badge`}>
              {okText}
            </Badge>
          ) : (
            <Badge variant="destructive" data-testid={`status-${label.toLowerCase()}-badge`}>
              {downText}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background px-4 py-3">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        {icon}
        <span>{label}</span>
      </div>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}
